import type { ReactNode, SVGProps } from "react";
import type { Color, Fill, Stroke } from "@deckloom/core/schema";

export interface PaintResult {
  /** value for the `fill` / `stroke` attribute */
  paint: string;
  opacity?: number;
  /** <defs> content that must be mounted for the paint to resolve */
  defs?: ReactNode;
}

export const rgba = (c: Color): string =>
  c.alpha >= 1 ? c.hex : `rgba(${parseInt(c.hex.slice(1, 3), 16)}, ${parseInt(c.hex.slice(3, 5), 16)}, ${parseInt(c.hex.slice(5, 7), 16)}, ${c.alpha})`;

export interface MediaSize { w?: number; h?: number }

export function fillPaint(fill: Fill | null | undefined, id: string, mediaUrl: (mediaId: string) => string, mediaSize?: (mediaId: string) => MediaSize | undefined): PaintResult {
  if (!fill || fill.kind === "none") return { paint: "none" };
  switch (fill.kind) {
    case "solid":
      return { paint: fill.color.hex, opacity: fill.color.alpha };
    case "gradient": {
      const gid = `g-${id}`;
      const stops = fill.stops.map((s, i) => <stop key={i} offset={s.pos} stopColor={s.color.hex} stopOpacity={s.color.alpha} />);
      if (fill.shape === "linear") {
        // OOXML angle: 0 = left->right, 90 = top->bottom (clockwise)
        const a = ((fill.angle ?? 0) * Math.PI) / 180;
        const x1 = 50 - 50 * Math.cos(a), y1 = 50 - 50 * Math.sin(a);
        const x2 = 50 + 50 * Math.cos(a), y2 = 50 + 50 * Math.sin(a);
        return {
          paint: `url(#${gid})`,
          defs: (
            <linearGradient id={gid} x1={`${x1}%`} y1={`${y1}%`} x2={`${x2}%`} y2={`${y2}%`}>
              {stops}
            </linearGradient>
          ),
        };
      }
      return {
        paint: `url(#${gid})`,
        defs: (
          <radialGradient id={gid} cx="50%" cy="50%" r="70%">
            {stops}
          </radialGradient>
        ),
      };
    }
    case "image": {
      const pid = `img-${id}`;
      const size = fill.mode === "tile" ? mediaSize?.(fill.mediaId) : undefined;
      if (fill.mode === "tile" && size?.w && size?.h) {
        // tiles keep the image's natural size (pixels at 96 dpi => pt) scaled by a:tile sx/sy
        const t = fill.tile ?? { tx: 0, ty: 0, sx: 1, sy: 1, align: "tl" };
        const tw = size.w * 0.75 * t.sx, th = size.h * 0.75 * t.sy;
        return {
          paint: `url(#${pid})`,
          defs: (
            <pattern id={pid} patternUnits="userSpaceOnUse" x={t.tx} y={t.ty} width={tw} height={th}>
              <image href={mediaUrl(fill.mediaId)} width={tw} height={th} preserveAspectRatio="none" opacity={fill.alpha ?? 1} />
            </pattern>
          ),
        };
      }
      return {
        paint: `url(#${pid})`,
        defs: (
          <pattern id={pid} patternUnits="objectBoundingBox" patternContentUnits="objectBoundingBox" width="1" height="1">
            <image href={mediaUrl(fill.mediaId)} width="1" height="1" preserveAspectRatio="none" opacity={fill.alpha ?? 1} />
          </pattern>
        ),
      };
    }
    case "pattern":
      return { paint: fill.fg.hex, opacity: fill.fg.alpha };
  }
}

export function strokeAttrs(stroke: Stroke | null | undefined, id: string, mediaUrl: (mediaId: string) => string): { attrs: SVGProps<SVGPathElement>; defs: ReactNode } {
  if (!stroke || stroke.width <= 0) return { attrs: { stroke: "none" }, defs: null };
  const p = fillPaint(stroke.fill, `s-${id}`, mediaUrl);
  const dash = Array.isArray(stroke.dash) ? stroke.dash.map((d) => d * stroke.width).join(" ") : dashArray(stroke.dash, stroke.width);
  return {
    attrs: {
      stroke: p.paint,
      strokeOpacity: p.opacity,
      strokeWidth: stroke.width,
      strokeDasharray: dash,
      strokeLinecap: stroke.cap === "flat" ? "butt" : stroke.cap,
      strokeLinejoin: stroke.join,
    },
    defs: p.defs ?? null,
  };
}

function dashArray(dash: string, w: number): string | undefined {
  const u = (n: number) => n * w;
  switch (dash) {
    case "dot": case "sysDot": return `${u(1)} ${u(1)}`;
    case "dash": case "sysDash": return `${u(3)} ${u(1)}`;
    case "lgDash": return `${u(8)} ${u(3)}`;
    case "dashDot": case "sysDashDot": return `${u(3)} ${u(1)} ${u(1)} ${u(1)}`;
    case "lgDashDot": return `${u(8)} ${u(3)} ${u(1)} ${u(3)}`;
    case "lgDashDotDot": case "sysDashDotDot": return `${u(8)} ${u(3)} ${u(1)} ${u(3)} ${u(1)} ${u(3)}`;
    default: return undefined;
  }
}
